import { FunctionComponent, ReactElement, useMemo } from 'react';
import { Customer, LocationStatistics, SingleSite } from '@/core/entities';
import { ALL_SITES_ID, useMostCleanedLocations } from '@/queries/logs';
import OverflowCell from '@/components/ui/table/components/OverflowCell';
import { Column } from '@/components/ui/table/types';
import { MostCleanedLocationResponse } from '@/api/dashboard/types';
import StatisticsTable from './StatisticsTable';
import { QueryDateRange } from '../../types';
import { SelectedLocation } from '../completed-cycles/filters/LocationFiltersDialog';
import { formatNumber } from '../../utils';

const LocationsCell = ({
	names,
	isPrintMode,
}: {
	names: LocationStatistics['names'];
	isPrintMode: boolean;
}) => {
	const locations = names?.join(' - ');
	return isPrintMode ? locations : <OverflowCell maxWidth={333}>{locations}</OverflowCell>;
};

const MostCleanedLocations: FunctionComponent<{
	site?: SingleSite;
	locationsFilter?: SelectedLocation[];
	locationId?: string;
	customer?: Pick<Customer, 'id' | 'name'>;
	isPrintMode?: boolean;
	showAllSitesData: boolean;
	isAdmin: boolean;
	dateRange: QueryDateRange;
}> = ({
	site,
	locationId,
	customer,
	isPrintMode,
	showAllSitesData,
	isAdmin,
	dateRange,
}): ReactElement => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const params: Record<string, any> = {
		fromDate: dateRange.start,
		toDate: dateRange.end,
	};

	if (customer) {
		params.customerId = customer.id;
	}

	if (locationId) {
		params.locationId = locationId;
	}
	const { query: mostCleanedLocationsQuery } = useMostCleanedLocations(
		params,
		showAllSitesData ? ALL_SITES_ID : site?.id
	);

	const isLoading = useMemo(
		() => mostCleanedLocationsQuery.isFetching,
		[mostCleanedLocationsQuery.isFetching]
	);

	const tableColumns = useMemo(() => {
		const columns: Column<MostCleanedLocationResponse>[] = [
			{
				name: 'Location',
				render: ({ customer_name, site_name, names }) => (
					<div>
						<div>
							<LocationsCell names={names || []} isPrintMode={!!isPrintMode} />
						</div>
						{isAdmin ? <small>{site_name || customer_name}</small> : <small>{site_name}</small>}
					</div>
				),
				accessors: ['customer_name', 'site_name', 'names'],
			},
			{
				name: 'Cycles',
				accessors: ['count'],
				align: 'right',
				render: ({ count }) => formatNumber(count),
			},
			{
				name: 'Completed',
				accessors: ['complete_count'],
				align: 'right',
				render: ({ complete_count }) => formatNumber(complete_count),
			},
		];
		return columns;
	}, [isAdmin, isPrintMode]);

	const sx = {
		'@media print': {
			margin: '20px',
			boxShadow: 'none',
			pageBreakInside: 'avoid',
			breakInside: 'avoid',
		},
	};

	return (
		<StatisticsTable<LocationStatistics>
			title="Most Cleaned Locations"
			query={mostCleanedLocationsQuery}
			columns={tableColumns}
			emptyState="No locations available"
			splitColumns={!isPrintMode}
			itemsPerPage={10}
			sx={sx}
			isLoading={isLoading}
		/>
	);
};

export default MostCleanedLocations;
